import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Home as HomeIcon, Building2, Store, MapPin, Pencil, Trash2, Star, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageTitle, BigButton } from "@/components/Layout";
import { AddressForm } from "@/components/AddressForm";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export const LABEL_ICON = { Home: HomeIcon, Office: Building2, Shop: Store, Other: MapPin };

const Row = ({ a, onEdit, onDelete, onDefault }) => {
  const { t } = useApp();
  const Icon = LABEL_ICON[a.label] || MapPin;
  const btn = "flex h-10 items-center gap-1 rounded-full border border-[#D2E0D8] px-3 text-xs font-semibold text-[#0D3B2B] active:scale-95 transition-transform";
  return (
    <Card className="space-y-3" data-testid={`address-${a.address_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#2E9E6B]/15"><Icon className="h-5 w-5 text-[#2E9E6B]" /></div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 font-bold text-[#0D3B2B]">{t(a.label)}
            {a.is_default && <span className="rounded-full bg-[#0D3B2B] px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`address-default-badge-${a.address_id}`}>{t("Default")}</span>}</p>
          <p className="text-sm text-[#4A6358]">{a.address}</p>
          {a.landmark && <p className="text-xs text-[#7B968B]">{a.landmark}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onEdit} className={btn} data-testid={`address-edit-${a.address_id}`}><Pencil className="h-3.5 w-3.5" />{t("Edit")}</button>
        {!a.is_default && <button onClick={onDefault} className={btn} data-testid={`address-make-default-${a.address_id}`}><Star className="h-3.5 w-3.5" />{t("Make default")}</button>}
        <button onClick={onDelete} className={`${btn} text-[#E05C3A]`} data-testid={`address-delete-${a.address_id}`}><Trash2 className="h-3.5 w-3.5" />{t("Delete")}</button>
      </div>
    </Card>
  );
};

export default function Addresses() {
  const { t, refreshUser } = useApp();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  useEffect(() => { api.get("/addresses").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const saved = (data) => { setRows(data); refreshUser?.(); };
  const act = async (fn, msg) => { try { saved((await fn()).data); toast.success(t(msg)); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <PageTitle sub={t("Save every place you give scrap from: home, office, shop. Pick one when you book.")}>{t("My addresses")}</PageTitle>
      <BigButton onClick={() => { setEdit(null); setOpen(true); }} data-testid="address-add-btn"><span className="inline-flex items-center gap-2"><Plus className="h-5 w-5" />{t("Add a new address")}</span></BigButton>
      {rows?.map((a) => <Row key={a.address_id} a={a} onEdit={() => { setEdit(a); setOpen(true); }} onDelete={() => setDel(a)}
        onDefault={() => act(() => api.post(`/addresses/${a.address_id}/default`), "Default address changed")} />)}
      {rows && !rows.length && <Card className="text-sm text-[#4A6358]" data-testid="address-empty">{t("No saved addresses yet.")}</Card>}
      <AddressForm open={open} onOpenChange={setOpen} initial={edit} onSaved={saved} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} danger title={t("Delete this address?")} lines={del ? [del.address] : []}
        onYes={async () => { await act(() => api.delete(`/addresses/${del.address_id}`), "Address deleted"); setDel(null); }} />
    </div>
  );
}
